import { ExpandableText } from "@/components/expandable-text";
import { Footer } from "@/components/footer";
import { ProjectGrid } from "@/components/project-grid";
import { ScrambledProjectGrid } from "@/components/scrambled-project-grid";
import { ScrambledSectionTitle } from "@/components/scrambled-section-title";
import { SectionTitle } from "@/components/section-title";
import { TextScramble } from "@/components/text-scramble";
import { FileText } from "lucide-react";
import Link from "@/components/link";
import { useEffect, useState } from "react";

export default function Home() {
  // State to track if the component has mounted
  const [isMounted, setIsMounted] = useState(false);

  // Set isMounted to true once component mounts
  useEffect(() => {
    setIsMounted(true);
  }, []);

  const projects = [
    {
      id: 2,
      title: "Diffusion Math",
      description: "Entire math behind diffusion models, by hand",
      image: "/images/diffusion-math.png",
      link: "/diffusion",
    },
    {
      id: 5,
      title: "A Gentle Intro to Flow Matching",
      description: "A gentle introduction to flow-based generative models",
      image: "/images/introtointroto.png",
      link: "/flow",
    },
    {
      id: 4,
      title: "miniSAM",
      description:
        "Distilling Segment Anything and making it GPU free!",
      image: "/images/sammy.png",
      link: "https://minisam.isaacbau.com",
    },
    {
      id: 6,
      title: "It's a Tiny World (Model)",
      description: "Playable world models that fit in your browser",
      image: "/images/world-model.png",
      link: "https://world.isaacbau.com",
    },
   {
      id: 3,
      title: "Tequila Clothes",
      description: "Tequila residues turned high fashion",
      image: "/images/tequila-clothes.jpg",
      link: "/tequila",
    },
    {
      id: 1,
      title: "Jingles of the World",
      description:
        "Beautiful melodies from all around the world, on my tiny piano",
      image: "/images/jingles-of-the-world.png",
      link: "https://jingles.isaacbau.com",
    },
  ];

  // One string per paragraph. Add, remove or reorder freely.
  const historyParagraphs = [
    "Born and raised in Mexico, I've been coding since I was 12. At 16 I was working at a VPS startup, mostly setting up custom architectures for clients.",

    "Got a scholarship to study Physics at the best Mexican private university. Started in Mexico City, later moved to Monterrey for some time. COVID hit and I spent a year in lockdown. Fortunately I had a nice reset by moving to Dresden, Germany to study abroad.",

    "With a DAAD Scholarship I studied Stochastic Processes, High Performance Computing and travelled all over the European continent. I created a beautiful app for all my classmates abroad which used ML to connect us all in Europe. I then took an internship at Porsche in Stuttgart to do ML for their electric fleet.",

    "Came back to finish my degree, then decided not to return to Europe or take a job in my country. Instead, with my savings and an ambitious feeling -- thanks to TheResidency -- I found a way to move to SF.",

    "After a biochem Tequila Clothes startup I started running Zepheon, a consulting agency with a funny origin story. I took a wide variety of projects and clients for a year while living in Toronto, Seoul, Tokyo and taking some time to backpack China.",

    "Had fun making multi embodiment navigation world models; similar to comma.ai but with robots intead of cars and sidewalks instead of highways.",

    "Now leading ML research and building 1M robots at Bracketbot.",

    "Certified fluent in Spanish, English, German and French. Learning Japanese as fast as possible.",

    "I like flying airplanes, science fiction, history, dancing salsa and exploring the strucuture of sound.",
  ];

  const expandableWords = [
    {
      id: "zepheon",
      word: "Zepheon",
      content: {
        text: "Zepheon was a consulting agency I started in San Francisco. It began when I was asked to help a team win a hackathon, which made me realize I could make money solving people's problems. We began with ML but did absolutely everything: from a Generative AI Studio for an Architecture Firm to developing an AI Engine for a legaltech startup. ML pipelines, frontend, backend, we did whatever the client needed!",
        imageUrl: "https://picsum.photos/206/150",
        imageWidth: 200,
        imageHeight: 150,
      },
    },
    {
      id: "kioete",
      word: "Tequila Clothes",
      content: {
        text: "Kioete: tequila high fashion! Got a blog on it",
        imageUrl: "/images/tequila-clothes.jpg",
        imageWidth: 200,
        imageHeight: 150,
      },
    },
    {
      id: "diffusion",
      word: "Diffusion Math",
      content: {
        text: "I created a comprehensive video explaining diffusion math from absolute scratch! Diffusion models are notorious for being mathematically complex, but I break down the concepts in an accessible way.",
        imageUrl: "/images/diffusion-math.png",
        imageWidth: 300,
        imageHeight: 300,
      },
    },
    {
      id: "flowmodels",
      word: "A Gentle Intro to Flow Matching",
      content: {
        text: "This is my introduction to the wonderful MIT lecture on flow models. Flow models are a fascinating class of generative models that transform simple distributions into complex ones through a series of invertible transformations. In this video, I break down the key concepts to make them more accessible.",
        imageUrl: "/images/introtointroto.png",
        imageWidth: 300,
        imageHeight: 300,
      },
    },
    {
      id: "sammy",
      word: "miniSAM",
      content: {
        text: "Fascinated by the idea of small local models, I looked into the implementation of Meta's Segment Anything Model and the tiny decoder they use to turn user inputs into segmentation points. I distilled the Vision Transformer encoder into a TinyViT variant, paired it with the decoder, and proved it can run entirely in-browser—no external compute.",
        imageUrl: "/images/sammy.png",
        imageWidth: 300,
        imageHeight: 200,
      },
    },
    {
      id: "physics",
      word: "Physics",
      content: {
        text: "Picture of me at graduation.\n\nMy university insists on calling 'Physics' by some absurd name for historic reasons. Despite the name, it's a normal physics curriculum -- The fact that I'm not a 'legal physicist' is very funny to my friends.",
        imageUrl: "/images/me-graduation.png",
        imageWidth: 250,
        imageHeight: 300,
      },
    },
    {
      id: "mexico",
      word: "Mexico",
      content: {
        text: "Danger, warmth and extremely good food: I never quite understood just how different my culture was until I moved away. I've unwantedly been in some horribly dangerous situations just as much as I've met the warmest and most incredible people. Truly a country of extremes, there's nothing quite like the chaos of Mexico.",
        imageUrl: "/images/taco.jpeg",
        imageWidth: 300,
        imageHeight: 200,
      },
    },
    {
      id: "porsche",
      word: "Porsche",
      content: {
        text: "Sincerely enjoyed my time at Porsche, I was part of the initial ML team. I helped developed algorithms for tracking and localization. I was personally in charge of the automatic model updates to the test fleet.",
        imageUrl: "/images/germany.jpeg",
        imageWidth: 200,
        imageHeight: 150,
      },
    },
    {
      id: "theresidency",
      word: "TheResidency",
      content: {
        text: "Accelerator program for founders and researchers. Met my now dearest friends there.",
        imageUrl: "/images/theresidency.jpeg",
        imageWidth: 200,
        imageHeight: 150,
      },
    },

    {
      id: "airplanes",
      word: "flying airplanes",
      content: {
        text: "I have an obsession with terrain and adore maps, this is what led me to find a way to get up in the air. I have only done very basic training, working on my licence.",
        imageUrl: "/images/airplane-window.png",
        imageWidth: 300,
        imageHeight: 200,
      },
    },
    {
      id: "science fiction",
      word: "science fiction",
      content: {
        text: "I love Isaac Asimov (first 3 Foundation, First 2 Robot Series, The Gods Themselves). I really like Ted Chiang, Dune, Snow Crash. Not scifi but I love Murakami, Saramago, Herman Hesse.",
        imageUrl: "https://picsum.photos/212/150",
        imageWidth: 200,
        imageHeight: 150,
      },
    },
    {
      id: "history",
      word: "history",
      content: {
        text: "History seen as a collection of dates to remember misses the point entirely. Like okok, let's take a simple question: why do we speak Spanish in Mexico? We do because the Catholic monarchs who sponsored Columbus's 'Discovery of America' spoke Castilian (there are multiple languages in Spain, and Castilian is one). Castilian as a main language spread after Isabella of Castile married Ferdinand of Aragon, a match that also united their kingdoms in the push to reclaim southern Spain from Muslim rule.\n\nWho were these Muslims in Spain?  Heirs of Umayyad refugees who left the Middle East when the Abbasids took power in the eighth century. The Abbasids are the same people who gave us algebra and trigonometry, and their musicians also refined the style for a 'phrygian' sound, the same cliche in movie scenes where a desert appears. This music eventually merged with local traditions and became flamenco (it uses phrygian dominant scales). The funny thing is, centuries later sailors and settlers took the style across the Atlantic, where in Mexico it blended with Indigenous/African beats and shaped the rancheras and sones that I've been listening to my entire childhood!\n\nMusic, Language, War, Mathematics, they are all related! The French Revolution isn't just an annoying thing to learn in high school: it's the creation of a 'nation'; Napoleon's campaigns aren't only thrilling but opened the door for Latin American independence; Greek victory over Persia and later defeat by Rome framed two thousand years of philosophy. I find this incredibly exciting!",
        imageUrl: "https://picsum.photos/209/150",
        imageWidth: 200,
        imageHeight: 150,
      },
    },
    {
      id: "fluent",
      word: "fluent",
      content: {
        text: "Fluency is hard to define. I don't have to think in these languages anymore, but I still have lots to learn (heavy pronunciation practice). I'm most comfortable in Spanish, English, and German, while my French hasn't been tested by long exposure in a francophone country. As for Japanese, I'm just excited about making progress and go back to talk with the friends I made",
        imageUrl: "/images/japan.jpeg",
        imageWidth: 300,
        imageHeight: 200,
      },
    },
    {
      id: "vps startup",
      word: "VPS startup",
      content: {
        text: "I was gifted a second hand Raspberry Pi at the time and found DevOps in Linux fascinating so I was mostly going into servers and installing LAMP (Linux, Apache, MySQL, PHP) type setups. Nothing too fancy, but I learned a lot about server administration, networking and, most importantly, understood that everything we interact with is sitting somewhere in a server rack",
        imageUrl: "https://picsum.photos/211/150",
        imageWidth: 200,
        imageHeight: 150,
      },
    },
    {
      id: "app",
      word: "app",
      content: {
        text: "During my time in Dresden, I created a social networking app specifically for international students. I had watched this great Stanford series on ML Graph Algorithms and decided to use them to match people based on interests, countries, and social connections. The app could suggest groups, travel destinations, and travel companions based on compatibility scores. It had things like event discovery, a global map, and a cool image editing to share your travels on Instagram. I wrote some blogs at the time about <LINK>https://isaacbau.github.io/2021/07/20/apps-are-easy.html</LINK>how I made the app<LINK> and about the <LINK>https://isaacbau.github.io/2023/08/10/predicting-friends.html</LINK>Graph ML<LINK>",
        imageUrl: "/images/suun.png",
        imageWidth: 300,
        imageHeight: 400,
      },
    },
    {
      id: "sound",
      word: "sounds",
      content: {
        text: "I've been learning music for about 2 years now and bought a cheap portable piano that I bring everywhere. Whenever I hear a cool sound, I recreate it.",
        imageUrl: "https://picsum.photos/215/150",
        imageWidth: 400,
        imageHeight: 500,
      },
    },
  ];

  return (
    <main className="min-h-screen bg-[#fbfaf8] flex flex-col">
      <header className="container mx-auto pt-8 pb-0">
        {isMounted ? (
          <h1 className="text-[5.5rem] sm:text-[6.5rem] font-extralight tracking-tight text-center">
            <TextScramble
              text="Isaac Bautista Silva"
              className="inline"
              speed={10}
              revealRate={0.15}
            />
          </h1>
        ) : (
          <h1 className="text-[5.5rem] sm:text-[6.5rem] font-extralight tracking-tight text-center">
            Isaac Bautista Silva
          </h1>
        )}
      </header>

      <div className="container mx-auto flex-grow pt-0 pb-8 px-6 md:px-12 lg:px-56 mt-4">
        {isMounted ? (
          <ScrambledProjectGrid projects={projects} />
        ) : (
          <ProjectGrid projects={projects} />
        )}

        <div className="mt-4 text-center">
          <Link
            href="https://txt.isaacbau.com"
            className="inline-flex items-center text-sm text-gray-500 italic hover:text-gray-800 transition-colors duration-200"
          >
            <FileText size={14} className="mr-1.5 opacity-70" />
            {isMounted ? (
              <TextScramble
                text="see all 20+ projects in plain text format at txt.isaacbau.com"
                className="inline"
                speed={40}
                revealRate={0.05}
              />
            ) : (
              <span>
                see all 20+ projects in plain text format at txt.isaacbau.com
              </span>
            )}
          </Link>
        </div>

        <div className="mt-24 space-y-16">
          <section>
            {isMounted ? (
              <ScrambledSectionTitle speed={35} revealRate={0.08}>
                Blog
              </ScrambledSectionTitle>
            ) : (
              <SectionTitle>Blog</SectionTitle>
            )}
            <div className="mt-8 space-y-2">
              <Link href="/blog/few-step" className="block">
                <h3 className="text-xl font-normal hover:underline">
                  Fast [Few-Single Step] Flow Matching
                </h3>
              </Link>
              <div className="py-1"></div>
              <Link href="/blog/flamenco" className="block">
                <h3 className="text-xl font-normal hover:underline">
                  Flamenco is the best music in the world and it's not even
                  close
                </h3>
              </Link>
            </div>
          </section>

          <section>
            {isMounted ? (
              <ScrambledSectionTitle speed={35} revealRate={0.08}>
                About
              </ScrambledSectionTitle>
            ) : (
              <SectionTitle>Story</SectionTitle>
            )}
            <div className="mt-8 pr-8 md:pr-24 lg:pr-64">
              <ExpandableText
                text={historyParagraphs}
                expandableWords={expandableWords}
              />
            </div>
          </section>
        </div>
      </div>

      <Footer />
    </main>
  );
}
